import React, { useState, useEffect } from 'react';
import { Card, Button, Form, Alert, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import OnboardingLayout from '../OnboardingLayout';
import AnimatedStep from './AnimatedStep';
import axios from '../../../api/axios';
import endpoints from '../../../api/endpoints';
import { toast, ToastContainer } from 'react-toastify';
import '../../../styles/Onboarding.css';
import { useSelector, useDispatch } from 'react-redux';
import { updateFormData } from '../../../redux/onboarding/onboardingSlice'; 

const StepFinalization = ({ data, setData, setStepValidity }) => {

  const dispatch = useDispatch();
  // Mandatory fields (always true and locked)
  const [acceptedTerms] = useState(true);
  const [acceptedPrivacy] = useState(true);
  const [profileVisibilityConsent] = useState(true);

  // Optional consents
  const [marketingConsent, setMarketingConsent] = useState(data.marketingConsent || true);
  const [phoneContactConsent, setPhoneContactConsent] = useState(data.phoneContactConsent || true);
  const [communityRulesConsent, setCommunityRulesConsent] = useState(data.communityRulesConsent || true);

  const [modals, setModals] = useState({
    agb: false,
    privacy: false,
    community: false
  });

  useEffect(() => {
    // Update Redux store using dispatch
    dispatch(updateFormData({
      ...data,  // keep existing data
      marketingConsent,
      phoneContactConsent,
      communityRulesConsent,
      profileVisibilityConsent: true,
    }));
    // This check ensures step validity is only updated once when all conditions are met
    setStepValidity(true);

  }, []);

  const tooltipIcon = (text) => (
    <OverlayTrigger
      placement="right"
      delay={{ show: 250, hide: 400 }}
      overlay={<Tooltip>{text}</Tooltip>}
    >
      <span role="button" tabIndex={0} style={{ marginLeft: 6, cursor: 'pointer', color: '#0d6efd' }}>
        ❓
      </span>
    </OverlayTrigger>
  );

  return (
    <OnboardingLayout>
      <ToastContainer position="top-center" />
      <AnimatedStep>
        <Card className="p-4 shadow-sm">
          <h4 className="mb-4">Letzter Schritt: Einverständnisse</h4>

          {/* ✅ AGB */}
          <Form.Group className="mb-3">
            <Form.Label><strong>AGB (Pflicht)</strong></Form.Label>
            <Form.Check
              type="checkbox"
              style={{fontSize: '0.9rem'}}
              label={
                <>
                  „Ich habe die Allgemeinen Geschäftsbedingungen (AGB) gelesen und akzeptiere sie.“{' '}
                  <Button variant="link" size="sm" onClick={() => setModals(prev => ({ ...prev, agb: true }))}>
                    (Lesen)
                  </Button>
                </>
              }
              checked={acceptedTerms}
              disabled
            />
          </Form.Group>

          {/* ✅ Datenschutz */}
          <Form.Group className="mb-3">
            <Form.Label><strong>Datenschutz (Pflicht)</strong></Form.Label>
            <Form.Check
              type="checkbox"
              style={{fontSize: '0.9rem'}}
              label={
                <>
                  „Ich habe die Datenschutzerklärung gelesen und stimme der Verarbeitung meiner personenbezogenen Daten zu.“{' '}
                  <Button variant="link" size="sm" onClick={() => setModals(prev => ({ ...prev, privacy: true }))}>
                    (Lesen)
                  </Button>
                </>
              }
              checked={acceptedPrivacy}
              disabled
            />
          </Form.Group>

          {/* 📧 Marketing Emails */}
          <Form.Group className="mb-3">
            <Form.Label><strong>Marketing E-Mails</strong></Form.Label>
            <Form.Check
              type="checkbox"
              style={{fontSize: '0.9rem'}}
              label={
                <>
                  Ich möchte per E-Mail über Angebote, neue Funktionen und Aktionen informiert werden.
                  {tooltipIcon("Du kannst dich jederzeit wieder abmelden. Einen Link zur Abmeldung findest du in jeder E-Mail.")}
                </>
              }
              checked={marketingConsent}
              onChange={(e) => setMarketingConsent(e.target.checked)}
            />
          </Form.Group>

          {/* 📱 Telefon/SMS Support */}
          <Form.Group className="mb-3">
            <Form.Label><strong>Kontaktmöglichkeiten (Telefon, SMS, Support)</strong></Form.Label>
            <Form.Check
              type="checkbox"
              style={{fontSize: '0.9rem'}}
              label={
                <>
                  Ich bin damit einverstanden, dass mich das Team per Telefon oder SMS für Support- und Serviceanfragen kontaktieren darf.
                  {tooltipIcon("Wir verwenden deine Telefonnummer nur für wichtige Hinweise und Support. Deine Daten werden vertraulich behandelt.")}
                </>
              }
              checked={phoneContactConsent}
              onChange={(e) => setPhoneContactConsent(e.target.checked)}
            />
          </Form.Group>

          {/* 🧑‍🤝‍🧑 Community Rules */}
          <Form.Group className="mb-3">
            <Form.Label><strong>Nutzungsbedingungen & Verhaltenskodex</strong></Form.Label>
            <Form.Check
              type="checkbox"
              style={{fontSize: '0.9rem'}}
              label={
                <>
                  Ich habe die Nutzungsbedingungen und den Verhaltenskodex gelesen und akzeptiere sie.
                  {tooltipIcon("Diese Regeln sorgen für ein respektvolles Miteinander in unserer Community.")}
                  <Button variant="link" size="sm" onClick={() => setModals(prev => ({ ...prev, community: true }))}>
                    (Lesen)
                  </Button>
                </>
              }
              checked={communityRulesConsent}
              onChange={(e) => setCommunityRulesConsent(e.target.checked)}
            />
          </Form.Group>

          {/* 🌍 Profile visibility */}
          <Form.Group className="mb-3">
            <Form.Label><strong>Profilfreigabe (Pflicht)</strong></Form.Label>
            <Form.Check
              type="checkbox"
              style={{fontSize: '0.9rem'}}
              label="Ich möchte, dass mein Profil sichtbar für andere Nutzer ist und freigegeben wird."
              checked={profileVisibilityConsent}
              disabled
            />
          </Form.Group>
        </Card>
      </AnimatedStep>

      {/* Modals */}
      <Modal show={modals.agb} onHide={() => setModals(prev => ({ ...prev, agb: false }))} centered size="lg">
        <Modal.Header closeButton><Modal.Title>Allgemeine Geschäftsbedingungen (AGB)</Modal.Title></Modal.Header>
        <Modal.Body>
          <p>[Hier erscheinen die Inhalte der AGB. Später aus JSON oder API laden.]</p>
        </Modal.Body>
      </Modal>

      <Modal show={modals.privacy} onHide={() => setModals(prev => ({ ...prev, privacy: false }))} centered size="lg">
        <Modal.Header closeButton><Modal.Title>Datenschutzerklärung</Modal.Title></Modal.Header>
        <Modal.Body>
          <p>[Hier erscheinen die Inhalte der Datenschutzerklärung. Später aus JSON oder API laden.]</p>
        </Modal.Body>
      </Modal>

      <Modal show={modals.community} onHide={() => setModals(prev => ({ ...prev, community: false }))} centered size="lg">
        <Modal.Header closeButton><Modal.Title>Nutzungsbedingungen & Verhaltenskodex</Modal.Title></Modal.Header>
        <Modal.Body>
          <p>[Hier erscheinen die Community-Regeln. Später aus JSON oder API laden.]</p>
        </Modal.Body>
      </Modal>
    </OnboardingLayout>
  );
};

export default StepFinalization;
